import { getCachedDateOfNow } from "#web/utils/getCachedDateOfNow";
import { LeakedCodeFilter } from "@gadget-client/keepcart-server";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import * as v from "valibot";

import { LeakedCode } from "./_models/leakedCode";
import { Order, OrdersUsingCodeWithinWeek } from "./_models/ordersUsingCodeWithinWeek";
import { codeUsageReader, leakedCodeReader } from "./_runtime";
import { CodeUsageReader } from "./codeUsageReader/service";
import { LeakedCodeReader } from "./leakedCodeReader/service";

const WEEKS_TO_FETCH = 8;

export const LeakedCodes = v.object({
  endCursor: v.nullable(v.string()),
  hasNextPage: v.boolean(),
  hasPreviousPage: v.boolean(),
  leakedCodes: v.array(
    v.object({
      ...LeakedCode.entries,
      averageWeeklyUsageAfterLeak: v.number(),
      averageWeeklyUsageBeforeLeak: v.number(),
      percentageDifference: v.number(),
    }),
  ),
  startCursor: v.nullable(v.string()),
});
export type LeakedCodes = v.InferOutput<typeof LeakedCodes>;

/** The result will be approximative in order to avoid outstanding results when the usage is close to 0. */
const computeWeeklyPercentageDifference = ({ after, before }: { after: number; before: number }) => {
  if (before === 0 && after === 0) {
    return 0;
  }
  if (before === 0) {
    return 100;
  }
  if (after === 0) {
    return -100;
  }

  return ((after - before) / before) * 100;
};

const getstart = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getend = (date: Date): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

/** Generate an array of weeks (key-value pair of start and end date) surrounding the leak date. */
const generateWeeks = ({
  leakedAt,
  numberOfWeeksAfter,
  numberOfWeeksBefore,
}: {
  leakedAt: Date;
  numberOfWeeksAfter: number;
  numberOfWeeksBefore: number;
}) => {
  const weeks: { end: Date; start: Date }[] = [];
  const leakstart = getstart(leakedAt);

  for (let i = -numberOfWeeksBefore; i < numberOfWeeksAfter; i++) {
    const start = new Date(leakstart);
    start.setDate(start.getDate() + i * 7);
    weeks.push({
      end: getend(start),
      start,
    });
  }

  return weeks;
};

const groupOrdersByWeek = ({
  orders,
  weeks,
}: {
  orders: Order[];
  weeks: { end: Date; start: Date }[];
}): OrdersUsingCodeWithinWeek[] => {
  return weeks.map(({ end, start }) => ({
    orders: orders.filter((order) => order.createdAt >= start && order.createdAt <= end),
    weekEnd: end,
    weekStart: start,
  }));
};

const computeAverageWeeklyUsage = ({ weeksWithOrders }: { weeksWithOrders: OrdersUsingCodeWithinWeek[] }) => {
  return weeksWithOrders.length === 0
    ? 0
    : weeksWithOrders.reduce((acc, weeklyUsage) => acc + weeklyUsage.orders.length, 0) / weeksWithOrders.length;
};

const excludeWeeksBeforeCodeStart = ({
  leakedCode,
  weeks,
}: {
  leakedCode: LeakedCode;
  weeks: { end: Date; start: Date }[];
}) => {
  const startDateOfWeeks = weeks[0]!.start;
  const codeStartsAt = leakedCode.codeStartsAt ?? new Date(0);
  const effectiveStartDate = Math.max.apply(Math, [codeStartsAt.getTime(), startDateOfWeeks.getTime()]);
  return weeks.filter(
    (week) => week.end.getTime() >= effectiveStartDate && week.end.getTime() <= leakedCode.leakedAt.getTime(),
  );
};

const excludeWeeksAfterCodeEndOrAfterToday = ({
  leakedCode,
  weeks,
}: {
  leakedCode: LeakedCode;
  weeks: { end: Date; start: Date }[];
}) => {
  const EXTRA_LONG_DATE_IN_FUTURE = new Date("3000-01-01T00:00:00.000Z");
  const dateOfNow = getCachedDateOfNow();
  const endDateOfWeeks = weeks[weeks.length - 1]!.end;
  const codeEndsAt = leakedCode.codeEndsAt ?? EXTRA_LONG_DATE_IN_FUTURE;
  const effectiveEndDate = Math.min.apply(Math, [codeEndsAt.getTime(), endDateOfWeeks.getTime(), dateOfNow.getTime()]);
  return weeks.filter(
    (week) => week.start.getTime() <= effectiveEndDate && week.end.getTime() >= leakedCode.leakedAt.getTime(),
  );
};

const aggregateUsageDataFromOrders = ({
  leakedCode,
  orders,
  weeks,
}: {
  leakedCode: LeakedCode;
  orders: Order[];
  weeks: { end: Date; start: Date }[];
}) => {
  const weeksBeforeLeak = excludeWeeksBeforeCodeStart({ leakedCode, weeks });
  const ordersByWeekBeforeLeak = groupOrdersByWeek({ orders, weeks: weeksBeforeLeak });
  const averageWeeklyUsageBeforeLeak = computeAverageWeeklyUsage({
    weeksWithOrders: ordersByWeekBeforeLeak,
  });

  const weeksAfterLeak = excludeWeeksAfterCodeEndOrAfterToday({ leakedCode, weeks });
  const ordersByWeekAfterLeak = groupOrdersByWeek({ orders, weeks: weeksAfterLeak });
  const averageWeeklyUsageAfterLeak = computeAverageWeeklyUsage({
    weeksWithOrders: ordersByWeekAfterLeak,
  });

  const percentageDifference = computeWeeklyPercentageDifference({
    after: averageWeeklyUsageAfterLeak,
    before: averageWeeklyUsageBeforeLeak,
  });

  return {
    averageWeeklyUsageAfterLeak,
    averageWeeklyUsageBeforeLeak,
    percentageDifference,
  };
};

const mergeLeakedCodeWithWeeklyUsage = (codeUsageReader: CodeUsageReader) => async (leakedCode: LeakedCode) => {
  const weeksSurroundingLeak = generateWeeks({
    leakedAt: leakedCode.leakedAt,
    numberOfWeeksAfter: WEEKS_TO_FETCH,
    numberOfWeeksBefore: WEEKS_TO_FETCH,
  });
  const startDateOfNWeeksBeforeLeak = weeksSurroundingLeak[0]!.start;
  const endDateOfNWeeksAfterLeak = weeksSurroundingLeak[weeksSurroundingLeak.length - 1]!.end;

  const ordersUsingCodeNWeeksSurroundingLeak = await codeUsageReader.getOrdersUsingCodeWithinDateRange({
    code: leakedCode.code,
    dateRange: {
      end: endDateOfNWeeksAfterLeak,
      start: startDateOfNWeeksBeforeLeak,
    },
    shopId: leakedCode.shopId,
  });
  const leakedCodeUsageData = aggregateUsageDataFromOrders({
    leakedCode,
    orders: ordersUsingCodeNWeeksSurroundingLeak,
    weeks: weeksSurroundingLeak,
  });

  return {
    ...leakedCode,
    ...leakedCodeUsageData,
  };
};

export const fetchLeakedCodes =
  ({ codeUsageReader, leakedCodeReader }: { codeUsageReader: CodeUsageReader; leakedCodeReader: LeakedCodeReader }) =>
  async (query: { after: null | string; before: null | string; filter: LeakedCodeFilter; search: null | string }) => {
    const leakedCodesPage = await leakedCodeReader.page(query);
    const leakedCodesPageWithUsage = await Promise.all(
      leakedCodesPage.leakedCodes.map(mergeLeakedCodeWithWeeklyUsage(codeUsageReader)),
    );

    return v.parse(LeakedCodes, {
      endCursor: leakedCodesPage.endCursor,
      hasNextPage: leakedCodesPage.hasNextPage,
      hasPreviousPage: leakedCodesPage.hasPreviousPage,
      leakedCodes: leakedCodesPageWithUsage,
      startCursor: leakedCodesPage.startCursor,
    } satisfies LeakedCodes);
  };

export interface LeakedCodePageQueryProps {
  after: null | string;
  before: null | string;
  filter: LeakedCodeFilter;
  search: null | string;
}

export const LeakedCodePageQuery = ({ after, before, filter, search }: LeakedCodePageQueryProps) =>
  queryOptions({
    enabled: !!filter,
    gcTime: 1000 * 60,
    placeholderData: keepPreviousData,
    queryFn: () => fetchLeakedCodes({ codeUsageReader, leakedCodeReader })({ after, before, filter, search }),
    queryKey: ["leakedCodes", after, before, filter, search],
    staleTime: 1000 * 60,
  });
